/**
 * Fire: the parts of vanilla's FireBlock random tick that are visible without
 * block states.
 *
 * Fire has no age property here (blocks are plain ids), so a flame counts the
 * updates it has run with nothing left to eat instead — a fire that finds no
 * flammable neighbour dies after a few updates, which is vanilla's behaviour on
 * bare stone. While fuel is in reach it:
 *   - spreads: a flammable neighbour is replaced by fire, so wooden builds are
 *     consumed outward from where the flame was struck (vanilla burns the block
 *     away and leaves fire in its place),
 *   - flickers out on its own after a while (vanilla ages fire to 15 and drops
 *     it), so a burning field does not stay alight forever,
 *   - burns forever on netherrack / magma, which vanilla keeps alight.
 *
 * Water is handled by the fluid sim, which flows into the (non-solid) fire cell
 * and replaces it — the same way vanilla rained-on water puts fire out.
 *
 * DOM-free: shared by the main thread only, but kept free of DOM types so it can
 * move to a worker later like fluid.ts.
 */

import { AIR, FIRE_ID, IS_FLAMMABLE, SUSTAINS_FIRE } from '../blocks';
import { mulberry32 } from '../noise';

/** Game ticks between two updates of the same flame (vanilla: 10..20). */
const TICK_MIN = 10;
const TICK_MAX = 20;
/** A flame with no fuel in reach dies after this many fruitless updates. */
const FUEL_LIFETIME = 4;
/** Per update, chance to light a flammable neighbour on fire. */
const SPREAD_CHANCE = 0.25;

/** 6-neighbourhood, used to notice flames next to a block change. */
const DX = [1, -1, 0, 0, 0, 0];
const DY = [0, 0, 1, -1, 0, 0];
const DZ = [0, 0, 0, 0, 1, -1];

export interface FireHost {
  getBlock(x: number, y: number, z: number): number;
  /** Is the cell inside the world and in a chunk that can be edited right now? */
  canEdit(x: number, y: number, z: number): boolean;
  /** Must report the change back through `FireSim.blockChanged`. */
  setBlock(x: number, y: number, z: number, id: number): void;
}

export class FireSim {
  /** Stop a game tick's updates after this long; the rest run next tick. */
  budgetMs = 2;

  private now = 0;
  // FIFO of scheduled updates (the delay range is small, so the order holds)
  private readonly queue: number[] = []; // x, y, z, due
  private head = 0;
  private readonly scheduled = new Set<number>();
  /** Updates a flame has run without finding fuel (its stand-in for age). */
  private readonly ages = new Map<number, number>();
  /** Candidate fuel cells for the current update. */
  private readonly fuel: number[] = [];
  private readonly rnd = mulberry32(0x51f1e5);

  constructor(private readonly host: FireHost) {}

  /** Flames waiting to update. */
  get pending(): number {
    return this.scheduled.size;
  }

  /** A fire block was placed: start ticking it. */
  ignite(x: number, y: number, z: number): void {
    this.schedule(x, y, z);
  }

  /** A block changed: flames there and beside it re-evaluate (new fuel, or the
   *  flame itself was replaced — say by water). */
  blockChanged(x: number, y: number, z: number): void {
    if (this.host.getBlock(x, y, z) === FIRE_ID) this.schedule(x, y, z);
    for (let d = 0; d < 6; d++) {
      if (this.host.getBlock(x + DX[d], y + DY[d], z + DZ[d]) === FIRE_ID) {
        this.schedule(x + DX[d], y + DY[d], z + DZ[d]);
      }
    }
  }

  /** One 20 Hz game tick: run every update that has come due. */
  tick(): void {
    this.now++;
    const q = this.queue;
    const t0 = performance.now();
    let n = 0;
    while (this.head < q.length && q[this.head + 3] <= this.now) {
      const x = q[this.head];
      const y = q[this.head + 1];
      const z = q[this.head + 2];
      this.head += 4;
      this.scheduled.delete(cellKey(x, y, z));
      this.update(x, y, z);
      if ((++n & 15) === 0 && performance.now() - t0 > this.budgetMs) break;
    }
    if (this.head === q.length) {
      q.length = 0;
      this.head = 0;
    } else if (this.head > 4096) {
      q.splice(0, this.head);
      this.head = 0;
    }
  }

  private schedule(x: number, y: number, z: number): void {
    const key = cellKey(x, y, z);
    if (this.scheduled.has(key)) return;
    this.scheduled.add(key);
    this.queue.push(x, y, z, this.now + TICK_MIN + ((this.rnd() * (TICK_MAX - TICK_MIN + 1)) | 0));
  }

  private update(x: number, y: number, z: number): void {
    const host = this.host;
    const key = cellKey(x, y, z);
    if (!host.canEdit(x, y, z) || host.getBlock(x, y, z) !== FIRE_ID) {
      this.ages.delete(key);
      return;
    }

    const eternal = SUSTAINS_FIRE[host.getBlock(x, y - 1, z)] === 1;
    const target = this.pickFuel(x, y, z);

    if (target < 0) {
      // nothing left to eat: the flame starves. Vanilla drops fire that finds
      // no flammable neighbour, so a burning build ends as flames dying out
      // over the holes they burned, and fire on bare stone only lasts seconds.
      if (!eternal) {
        const age = (this.ages.get(key) ?? 0) + 1;
        if (age >= FUEL_LIFETIME) {
          this.ages.delete(key);
          host.setBlock(x, y, z, AIR);
          return;
        }
        this.ages.set(key, age);
      }
    } else {
      this.ages.delete(key);
      if (this.rnd() < SPREAD_CHANCE) {
        // the fuel block itself catches: vanilla consumes it and leaves fire
        host.setBlock(this.fuel[target], this.fuel[target + 1], this.fuel[target + 2], FIRE_ID);
      }
    }
    this.schedule(x, y, z);
  }

  /** Start index in `fuel` of a random flammable neighbour, or -1 for none.
   *  Vanilla samples the 3x3x3 around the flame; the cells are collected and one
   *  is chosen at random, so a pile of fuel catches faster than a single block. */
  private pickFuel(x: number, y: number, z: number): number {
    const host = this.host;
    const fuel = this.fuel;
    fuel.length = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0 && dz === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          const nz = z + dz;
          if (IS_FLAMMABLE[host.getBlock(nx, ny, nz)] !== 1) continue;
          if (!host.canEdit(nx, ny, nz)) continue;
          fuel.push(nx, ny, nz);
        }
      }
    }
    if (fuel.length === 0) return -1;
    return (((this.rnd() * fuel.length) / 3) | 0) * 3;
  }
}

/** Integer key of a block cell (|x|, |z| < 2^20, y < 256). */
function cellKey(x: number, y: number, z: number): number {
  return ((x + 0x100000) * 0x200000 + (z + 0x100000)) * 256 + y;
}
